import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService, persistSession } from '../services/authService';

const AuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    message: 'Signing you in...'
  });

  useEffect(() => {
    let isCancelled = false;

    const getHashParams = () => {
      const rawHash = window.location.hash || '';
      const hash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
      const normalized = hash.startsWith('?') ? hash.slice(1) : hash;
      return new URLSearchParams(normalized);
    };

    const failAndRedirect = (message) => {
      if (isCancelled) return;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setState({ loading: false, message });
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    };

    const processCallback = async () => {
      const hashParams = getHashParams();
      const token = searchParams.get('token') || hashParams.get('token');
      if (!token) {
        failAndRedirect('Google callback is missing token. Redirecting to login...');
        return;
      }

      try {
        const error = searchParams.get('error') || hashParams.get('error');
        if (error) {
          failAndRedirect(error);
          return;
        }

        const userEncoded = searchParams.get('user') || hashParams.get('user');
        if (userEncoded) {
          try {
            const user = JSON.parse(userEncoded);
            persistSession({ success: true, token, user });
          } catch (_) {
            const user = JSON.parse(decodeURIComponent(userEncoded));
            persistSession({ success: true, token, user });
          }
        } else {
          localStorage.setItem('token', token);
          const me = await authService.getMe();
          if (!me?.success || !me?.data) {
            throw new Error('Failed to load your profile after Google login.');
          }
          persistSession({ success: true, token, user: me.data });
        }

        if (isCancelled) return;
        setState({ loading: false, message: 'Login successful. Redirecting...' });
        window.location.replace('/dashboard');
      } catch (error) {
        failAndRedirect(error?.response?.data?.message || error?.message || 'Failed to complete Google login.');
      }
    };

    processCallback();

    return () => {
      isCancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Google Sign-In</h2>
        <p className={`${state.loading ? 'text-gray-600' : 'text-red-600'}`}>{state.message}</p>
        {!state.loading && (
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="mt-4 text-primary hover:underline"
          >
            Back to Login
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
